import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ArrowLeft, Search, UserPlus, Check, X, Loader2, Users, Inbox, Clock, Globe2, Trophy, Activity, Footprints } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { toast } from 'sonner';
import { FloatingActionButton } from '../../components/common/FloatingActionButton';
import { FriendInviteDialog } from '../../components/common/FriendInviteDialog';
import { useUnits, kmToDisplay, unitLabel } from '../../lib/units';
import { VerifiedBadge } from '../../components/common/Badges';

export default function Friends() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialTab = params.get('tab') === 'requests'
    ? 'requests'
    : params.get('tab') === 'global' ? 'global' : 'friends';
  const [q, setQ] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const { units } = useUnits();

  const { data: friends, loading: friendsLoading, refresh: refreshFriends } = useApi(api.getFriends);
  const { data: requests, loading: reqLoading, refresh: refreshRequests } = useApi(api.getFriendRequests);
  const { data: globalFeed, loading: globalLoading } = useApi(() => api.getFriendsAndOwnRunsFeed?.({ limit: 30 }), []);

  const filtered = (friends || []).filter((f) => f.name.toLowerCase().includes(q.toLowerCase()));
  const pendingCount = (requests || []).length;

  async function handleAccept(requesterId) {
    try {
      await api.friendAccept(requesterId);
      toast.success('Friend added');
      refreshRequests();
      refreshFriends();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function handleDecline(requesterId) {
    try {
      await api.friendDecline(requesterId);
      toast.success('Request declined');
      refreshRequests();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="friends-screen">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="friends-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <p className="rivl-label">Network</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Friends</h1>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="grid grid-cols-3 h-auto p-1 rounded-md">
          <TabsTrigger value="friends" data-testid="friends-tab-list" className="text-xs uppercase tracking-widest gap-1.5">
            <Users className="h-3.5 w-3.5" /> Friends
          </TabsTrigger>
          <TabsTrigger value="global" data-testid="friends-tab-global" className="text-xs uppercase tracking-widest gap-1.5">
            <Globe2 className="h-3.5 w-3.5" /> Global
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="friends-tab-requests" className="text-xs uppercase tracking-widest gap-1.5">
            <Inbox className="h-3.5 w-3.5" /> Requests
            {pendingCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1" data-testid="friends-requests-count">
                {pendingCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="friends" className="mt-4 space-y-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find friends" className="pl-9 h-11 rounded-md" data-testid="friends-search" />
          </div>

          {friendsLoading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!friendsLoading && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">No friends yet. Find runners on the leaderboard.</p>
          )}
          {!friendsLoading && filtered.length > 0 && (
            <div className="rivl-card divide-y divide-border" data-testid="friends-list">
              {filtered.map((f) => (
                <div key={f.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={f.name} src={f.avatar} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{f.handle}</div>
                  </div>
                  {f.status === 'pending' ? (
                    <span
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-dashed border-muted-foreground/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground"
                      data-testid={`friend-status-${f.id}`}
                    >
                      <Clock className="h-3 w-3" /> Pending
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-8 rounded-md" data-testid={`friend-status-${f.id}`}>
                      <Check className="h-3 w-3 mr-1" /> Friend
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="global" className="mt-4 space-y-3" data-testid="friends-global-feed">
          {globalLoading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!globalLoading && (globalFeed || []).length === 0 && (
            <div className="rivl-card p-8 text-center">
              <Activity className="h-6 w-6 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">No runs yet from you or your friends.</p>
              <p className="text-[11px] text-muted-foreground mt-1">Once you (or anyone you follow) finish a run, it'll show up here.</p>
            </div>
          )}
          {!globalLoading && (globalFeed || []).length > 0 && (
            <div className="rivl-card divide-y divide-border">
              {globalFeed.map((r) => (
                <FeedRow key={r.id} run={r} units={units} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-3">
          {reqLoading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!reqLoading && pendingCount === 0 && (
            <div className="rivl-card p-8 text-center">
              <Inbox className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">No pending requests.</p>
            </div>
          )}
          {!reqLoading && pendingCount > 0 && (
            <div className="rivl-card divide-y divide-border" data-testid="friend-requests-list">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`friend-request-${r.id}`}>
                  <Avatar name={r.name} src={r.avatar} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.handle}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handleDecline(r.id)} size="sm" variant="outline" className="h-8 w-8 p-0 rounded-md" data-testid={`friend-decline-${r.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                    <Button onClick={() => handleAccept(r.id)} size="sm" className="h-8 rounded-md" data-testid={`friend-accept-${r.id}`}>
                      <UserPlus className="h-3 w-3 mr-1" /> Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <FloatingActionButton
        onClick={() => setInviteOpen(true)}
        label="Invite a friend"
        testid="friends-fab-invite"
      />
      <FriendInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSent={() => { refreshFriends(); refreshRequests(); }}
      />
    </div>
  );
}

function Avatar({ name, src }) {
  const [broken, setBroken] = useState(false);
  const initials = (name || 'U').split(' ').map((n) => n[0]).slice(0, 2).join('');
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || ''}
        onError={() => setBroken(true)}
        className="h-10 w-10 rounded-md border border-border object-cover"
      />
    );
  }
  return (
    <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center text-sm font-bold">
      {initials}
    </div>
  );
}

function FeedRow({ run, units }) {
  const distance = `${kmToDisplay(run.distanceKm || 0, units).toFixed(2)} ${unitLabel(units)}`;
  const duration = formatDur(run.durationSec);
  return (
    <div className="flex items-start gap-3 px-4 py-3" data-testid={`global-feed-row-${run.id}`}>
      <Avatar name={run.userName} src={run.avatarUrl} />
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug">
          <span className="font-semibold">{run.userName}</span>{' '}
          <span className="text-muted-foreground">ran {distance} · {duration}</span>
        </p>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{relTime(run.createdAt)}</span>
          {run.verified && <VerifiedBadge />}
          {run.type === 'finish' && <span className="inline-flex items-center gap-0.5"><Trophy className="h-3 w-3" /> Finish</span>}
        </div>
      </div>
      <Footprints className="h-4 w-4 text-muted-foreground mt-1.5 flex-shrink-0" />
    </div>
  );
}

function formatDur(sec) {
  if (!sec || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}:${String(s).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function relTime(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}
